/* eslint-disable max-lines */
import { createApi } from '@reduxjs/toolkit/query/react';
import {
    type BenefitType,
    FMH_NPI,
    FMH_UNMANAGED_NPI,
    type ReferentPriceType,
    type RouteCode,
} from '@strata/constants';
import { capitalizeString, formatNameLastFirst, sortDateDesc } from '@strata/helper-functions';
import { type Answer } from '@strata/react-question-tree';
import {
    type ReferralStatusIndicator,
    TreatmentStatusIndicator,
} from '@strata/react-status-indicators';

import { TREATMENT_API_URL } from 'api/APIBase';
import { handleError, handleRTKQueryError } from 'services/Error';
import { getBaseQuery } from 'store/api/apiBaseQuery';

/** Object containing the summary of a treatment referral */
export interface TreatmentReferralSummary {
    /** The referral ID */
    referralId: string;
    /** The friendly ID */
    friendlyId?: string;
    /** The auth ID */
    authId?: string;
    /** The servicing supplier NPI */
    servicingSupplierNpi?: string;
    /** The route name */
    routeName?: string;
    /** The route code */
    routeCode?: RouteCode;
    /** The benefit type */
    benefitType?: string;
    /** The unformatted benefit type */
    benefitTypeRaw?: BenefitType;
    /** The received time */
    receivedTime: string;
    /** The referral status */
    referralStatus: (typeof ReferralStatusIndicator.Statuses)[keyof typeof ReferralStatusIndicator.Statuses];
}

/** Object containing the details of a treatment auth */
export interface TreatmentAuth {
    /** The case ID */
    caseId: string;
    /** The authorization start date */
    authStart: string;
    /** The authorization expiration date */
    authExpiration: string;
    /** The billing provider */
    billingProvider?: string;
    /** The servicing provider */
    servicingProvider?: string;
    /** The requesting provider */
    requestingProvider?: string;
    /** The servicing facility */
    servicingFacility?: string;
}

/** Object containing the details of a treatment */
export interface TreatmentDetails {
    /** The payer ID */
    payerId: string;
    /** The treatment ID */
    treatmentId: string;
    /** The segment ID */
    segmentId: string;
    /** Whether the product is brand or generic */
    isBrand: boolean;
    /** The benefit type */
    benefitType: BenefitType;
    /** The place of service */
    placeOfService?: string;
    /** The product service OID */
    productServiceOid: string;
    /** The treatment status */
    treatmentStatus: (typeof TreatmentStatusIndicator.Statuses)[keyof typeof TreatmentStatusIndicator.Statuses];
    /** Whether the treatment is active */
    isActive: boolean;
    isContingentTherapy?: boolean;
    /** The first name of the patient */
    firstName: string;
    /** The last name of the patient */
    lastName: string;
    /** The active referral summary */
    activeReferral?: Pick<
        TreatmentReferralSummary,
        | 'referralId'
        | 'authId'
        | 'benefitType'
        | 'benefitTypeRaw'
        | 'servicingSupplierNpi'
        | 'routeCode'
    > & { receivedTime: string | null };
    /** The list of referral summaries */
    referralList: TreatmentReferralSummary[];
    /** The list of auth summaries */
    authList: TreatmentAuth[];
    /** The member details summary */
    memberDetails: {
        /** The member ID */
        memberId: string;
        /** The first name of the member */
        firstName: string;
        /** The last name of the member */
        lastName: string;
        /** The date of birth of the member */
        dateOfBirth: string;
        /** The benefit group of the member */
        benefitGroup?: string;
        /** The benefit group name of the member */
        benefitGroupName?: string;
        /** The carrier of the member */
        carrier?: string;
        /** The account of the member */
        account?: string;
    };
}

export interface ReferralDetails {
    referralId: string;
    treatmentId: string;
    segmentId: string;
    productServiceOid: string;
    isBrand?: boolean;
    diagnosisOid: string;
    memberId: string;
    memberDOB: string;
    memberFirstName: string;
    memberLastName: string;
    memberName: string;
    winningBidRate?: string;
    referentPriceType?: ReferentPriceType;
    receivedTime: string;
    referralExpiration?: string;
    contractedDate?: string;
    referralStart?: string;
    outreachDate?: string;
    routeCode?: RouteCode;
    referralStatus: (typeof ReferralStatusIndicator.Statuses)[keyof typeof ReferralStatusIndicator.Statuses];
    payerId: string;
    placeOfService?: string;
    prescriberNpi?: string;
    practiceNpi?: string;
    supplierNpi?: string;
    clinicalInfo: { clinicalPolicyAnswers?: Answer[] };
    benefit: BenefitType;
    friendlyId?: string;
}

export const treatmentApi = createApi({
    baseQuery: getBaseQuery(TREATMENT_API_URL),
    refetchOnMountOrArgChange: true,
    reducerPath: 'treatmentApi',
    endpoints: (build) => ({
        getTreatmentDetails: build.query<
            TreatmentDetails,
            { payerId: string; treatmentId: string }
        >({
            query: ({ payerId, treatmentId }) => `${payerId}/treatments/${treatmentId}`,
            transformResponse: (response: {
                treatment_id: string;
                payer_id: string;
                segment_id: string;
                member: {
                    member_id: string;
                    first_name: string;
                    last_name: string;
                    dob: string;
                    benefit_group?: string;
                    benefit_group_name?: string;
                    carrier?: string;
                    account?: string;
                };
                product_service_id: string;
                product_service_oid: string;
                benefit_type: BenefitType;
                treatment_status: (typeof TreatmentStatusIndicator.Statuses)[keyof typeof TreatmentStatusIndicator.Statuses];
                is_brand?: boolean;
                place_of_service?: string;
                is_contingent_therapy?: boolean;
                active_referral?: {
                    referral_id: string;
                    benefit_type: BenefitType;
                    route_code?: RouteCode;
                    servicing_info?: {
                        servicing_parent_pharmacy_npi?: string;
                    };
                    auths: { auth_id: string; auth_expiration: string }[];
                };
                referrals: {
                    referral_id: string;
                    friendly_id?: string;
                    auth_id?: string;
                    servicing_parent_pharmacy_npi?: string;
                    route_name?: string;
                    route_code?: RouteCode;
                    benefit_type?: BenefitType;
                    created_at: string;
                    referral_status: (typeof ReferralStatusIndicator.Statuses)[keyof typeof ReferralStatusIndicator.Statuses];
                }[];
                auths: {
                    auth_id: string;
                    auth_start: string;
                    auth_expiration: string;
                    billing_provider?: string;
                    servicing_provider?: string;
                    requesting_provider?: string;
                    servicing_facility?: string;
                }[];
            }) => {
                const referralList = response.referrals.map((referral) => ({
                    referralId: referral.referral_id,
                    friendlyId: referral.friendly_id,
                    authId: referral.auth_id,
                    servicingSupplierNpi:
                        referral.servicing_parent_pharmacy_npi &&
                        ![FMH_NPI, FMH_UNMANAGED_NPI].includes(
                            referral.servicing_parent_pharmacy_npi,
                        )
                            ? referral.servicing_parent_pharmacy_npi
                            : undefined,
                    routeName: referral.route_name,
                    routeCode: referral.route_code,
                    benefitType: capitalizeString(referral.benefit_type),
                    benefitTypeRaw: referral.benefit_type,
                    receivedTime: referral.created_at,
                    referralStatus: referral.referral_status,
                }));
                const matchedActiveReferral = response.referrals.find(
                    (r) => r.referral_id === response.active_referral?.referral_id,
                );
                const activeReferralServicingNpi =
                    response.active_referral?.servicing_info?.servicing_parent_pharmacy_npi;
                const activeReferral = response.active_referral
                    ? {
                          referralId: response.active_referral.referral_id,
                          authId: response.active_referral.auths?.sort((a, b) =>
                              sortDateDesc(a.auth_expiration, b.auth_expiration),
                          )[0]?.auth_id,
                          benefitType: capitalizeString(response.active_referral.benefit_type),
                          benefitTypeRaw: response.active_referral.benefit_type,
                          receivedTime: matchedActiveReferral?.created_at ?? null,
                          routeCode: response.active_referral.route_code,
                          servicingSupplierNpi:
                              activeReferralServicingNpi &&
                              ![FMH_NPI, FMH_UNMANAGED_NPI].includes(activeReferralServicingNpi)
                                  ? activeReferralServicingNpi
                                  : undefined,
                      }
                    : undefined;

                return {
                    payerId: response.payer_id,
                    treatmentId: response.treatment_id,
                    segmentId: response.segment_id,
                    isBrand: !!response.is_brand,
                    benefitType: response.benefit_type,
                    placeOfService: response.place_of_service,
                    productServiceOid: response.product_service_oid,
                    treatmentStatus: response.treatment_status,
                    isActive:
                        response.treatment_status === TreatmentStatusIndicator.Statuses.ACTIVE,
                    isContingentTherapy: response.is_contingent_therapy,
                    firstName: response.member.first_name,
                    lastName: response.member.last_name,
                    activeReferral,
                    referralList,
                    authList: response.auths.map((auth) => ({
                        caseId: auth.auth_id,
                        authStart: auth.auth_start,
                        authExpiration: auth.auth_expiration,
                        billingProvider: auth.billing_provider,
                        servicingProvider: auth.servicing_provider,
                        requestingProvider: auth.requesting_provider,
                        servicingFacility: auth.servicing_facility,
                    })),
                    memberDetails: {
                        memberId: response.member.member_id,
                        firstName: response.member.first_name,
                        lastName: response.member.last_name,
                        dateOfBirth: response.member.dob,
                        benefitGroup: response.member.benefit_group,
                        benefitGroupName: response.member.benefit_group_name,
                    },
                };
            },
            transformErrorResponse: (err) => {
                if (err.status === 404) {
                    handleError('Treatment not found.', err);
                } else {
                    handleError('An error occurred retrieving treatment details.', err);
                }
            },
        }),
        getReferralDetails: build.query<ReferralDetails, { payerId: string; referralId: string }>({
            query: ({ payerId, referralId }) => `${payerId}/referrals/${referralId}`,
            // eslint-disable-next-line complexity
            transformResponse: (response: {
                referral_id: string;
                treatment_id: string;
                segment_id: string;
                payer_id: string;
                product_service_oid: string;
                is_brand?: boolean;
                diagnosis_oid: string;
                friendly_id?: string;
                place_of_service?: string;
                benefit_type: BenefitType;
                referral_status: string;
                created_at: string;
                contracted_date?: string;
                outreach_date?: string;
                route_code?: RouteCode;
                member: { member_id: string; first_name: string; last_name: string; dob: string };
                referral_period?: { start?: string; expiration: string };
                servicing_info?: {
                    servicing_parent_pharmacy_npi: string;
                    servicing_rate: { rate?: string; referent_price_type?: ReferentPriceType };
                };
                prescription_info?: { prescriber_npi?: string; practice_npi?: string };
            }) => ({
                referralId: response?.referral_id,
                treatmentId: response?.treatment_id,
                segmentId: response?.segment_id,
                productServiceOid: response?.product_service_oid,
                isBrand: response?.is_brand,
                diagnosisOid: response?.diagnosis_oid,
                memberId: response?.member?.member_id,
                memberDOB: response?.member?.dob,
                memberFirstName: response?.member?.first_name,
                memberLastName: response?.member?.last_name,
                memberName: formatNameLastFirst(
                    response?.member?.last_name,
                    response?.member?.first_name,
                ),
                winningBidRate: response?.servicing_info?.servicing_rate?.rate,
                referentPriceType: response?.servicing_info?.servicing_rate?.referent_price_type,
                receivedTime: response?.created_at,
                referralExpiration: response?.referral_period?.expiration,
                contractedDate: response?.contracted_date,
                referralStart: response?.referral_period?.start,
                outreachDate: response?.outreach_date,
                routeCode: response?.route_code,
                referralStatus:
                    response?.referral_status?.trim() as (typeof ReferralStatusIndicator.Statuses)[keyof typeof ReferralStatusIndicator.Statuses],
                payerId: response?.payer_id,
                placeOfService: response?.place_of_service?.trim(),
                prescriberNpi: response?.prescription_info?.prescriber_npi,
                practiceNpi: response?.prescription_info?.practice_npi,
                supplierNpi: response?.servicing_info?.servicing_parent_pharmacy_npi,
                clinicalInfo: JSON.parse('{}') as ReferralDetails['clinicalInfo'], // TODO update feature
                friendlyId: response?.friendly_id,
                benefit: response?.benefit_type?.trim() as BenefitType,
            }),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving referral details.',
            ),
        }),
    }),
});

export const { useGetTreatmentDetailsQuery, useGetReferralDetailsQuery } = treatmentApi;
